/// <reference lib="dom" />
/**
 * Live expression preview (M6) — upgrades the parent-side snapshot evaluation
 * (services/preview-eval.ts) to LIVE-context evaluation inside the canvas iframe, over the
 * `evalExpr`/`evalResult` bridge. The iframe evaluates against the real resolved scope (real
 * repeater items, real `window#/` globals, uncapped values); the snapshot evaluator stays the
 * fallback whenever no iframe host can answer (canvas not mounted, workspace takeover, timeout,
 * stale-gen reply).
 *
 * Call sites stay synchronous: {@link livePreviewExpression} returns the freshest available preview
 * immediately (the retained live result when it matches the expression, else the snapshot
 * evaluation) and kicks a debounced live refresh; when a CHANGED live result lands it is stored on
 * `tab.session.canvas.livePreviews` (mirroring how `canvas.scope` is stored) and the caller's
 * `onUpdate` re-renders the panel through its existing scheduler.
 */

import { isMutating } from "@jxsuite/runtime/expression";
import { previewExpression } from "./preview-eval";
import { requestCanvasEval } from "../canvas/iframe-host";

import type { ExpressionPreview } from "./preview-eval";
import type { StoredLivePreview, Tab } from "../tabs/tab";

/** Debounce (ms) between a call site's render and the posted evalExpr — the presence-measure idiom. */
export const LIVE_PREVIEW_DEBOUNCE_MS = 100;

/** Pending debounced refreshes keyed by `tabId targetId` (latest expression wins). */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** The staleness key a stored live result is checked against: the expression + its context. */
function exprKeyOf(node: unknown, contextPath: (string | number)[] | null): string {
  return JSON.stringify([node, contextPath]);
}

/** Whether `node` is an evaluable expression node (same gate as previewExpression). */
function isExprNode(node: unknown): node is { operator: string } {
  return Boolean(node && typeof node === "object" && "operator" in node);
}

/** Rebuild an ExpressionPreview from stored/wire values (mutating derives from the node itself). */
function toPreview(
  node: { operator: string },
  values: [string, string][],
  error: string | null,
): ExpressionPreview {
  return { error, mutating: isMutating(node.operator), values: new Map(values) };
}

/**
 * Ask the canvas iframe to evaluate `node` live. Resolves null when no host answers (no iframe for
 * this tab, ~300ms timeout, or a stale-gen reply) — the caller falls back to the snapshot.
 */
async function fetchLivePreview(
  tab: Tab,
  node: { operator: string },
  contextPath: (string | number)[] | null,
): Promise<ExpressionPreview | null> {
  const results = await requestCanvasEval(tab.id, [{ id: "0", node }], contextPath);
  const result = results?.find((r) => r.id === "0");
  return result ? toPreview(node, result.values, result.error ?? null) : null;
}

/**
 * Evaluate `node` for display, preferring the live canvas scope: live-context evaluation in the
 * iframe when a host answers within the timeout, else the parent-side snapshot evaluation. Returns
 * null when `node` is not an expression node or no evaluation basis exists at all.
 */
export async function requestLivePreview(
  tab: Tab,
  node: unknown,
  contextPath: (string | number)[] | null = null,
): Promise<ExpressionPreview | null> {
  if (!isExprNode(node)) {
    return null;
  }
  const live = await fetchLivePreview(tab, node, contextPath);
  return live ?? previewExpression(node, tab.session.canvas.scope);
}

/**
 * Synchronous call-site wrapper. Returns the retained live preview when it matches this exact
 * expression+context, else the snapshot evaluation — and (debounced) refreshes the live result in
 * the background, invoking `onUpdate` only when a CHANGED result lands so re-render loops cannot
 * self-sustain (an identical refresh stores silently and the chain stops).
 */
export function livePreviewExpression(
  tab: Tab | null | undefined,
  targetId: string,
  node: unknown,
  contextPath: (string | number)[] | null = null,
  onUpdate?: () => void,
): ExpressionPreview | null {
  if (!tab || !isExprNode(node)) {
    return null;
  }
  const key = exprKeyOf(node, contextPath);
  scheduleLiveRefresh(tab, targetId, node, contextPath, key, onUpdate);
  const stored = tab.session.canvas.livePreviews?.[targetId];
  if (stored && stored.key === key) {
    return toPreview(node, stored.values, stored.error);
  }
  return previewExpression(node, tab.session.canvas.scope);
}

/** Debounce a live refresh for (tab, target); the latest expression/context/callback wins. */
function scheduleLiveRefresh(
  tab: Tab,
  targetId: string,
  node: { operator: string },
  contextPath: (string | number)[] | null,
  key: string,
  onUpdate?: () => void,
): void {
  const timerKey = `${tab.id} ${targetId}`;
  const existing = timers.get(timerKey);
  if (existing) {
    clearTimeout(existing);
  }
  timers.set(
    timerKey,
    setTimeout(() => {
      timers.delete(timerKey);
      void (async () => {
        const live = await fetchLivePreview(tab, node, contextPath);
        if (!live) {
          return; // No live answer — the call site keeps rendering the snapshot values.
        }
        const stored: StoredLivePreview = {
          error: live.error,
          key,
          values: [...live.values.entries()],
        };
        const prev = tab.session.canvas.livePreviews?.[targetId];
        const changed =
          !prev ||
          prev.key !== key ||
          prev.error !== stored.error ||
          JSON.stringify(prev.values) !== JSON.stringify(stored.values);
        (tab.session.canvas.livePreviews ??= {})[targetId] = stored;
        if (changed) {
          onUpdate?.();
        }
      })();
    }, LIVE_PREVIEW_DEBOUNCE_MS),
  );
}
